import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sellStore } from "../../store/sellStore";
import { authStore } from "../../store/authStore";
import { sendOTP, verifyOTP } from "../../services/api";

interface Props { open: boolean; onClose: () => void; }

export function OtpModal({ open, onClose }: Props) {
  const [step, setStep]     = useState<"phone" | "otp">("phone");
  const [phone, setPhone]   = useState("");
  const [otp, setOtp]       = useState("");
  const [timer, setTimer]   = useState(0);
  const [error, setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const iRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (timer <= 0) return;
    iRef.current = setInterval(() => setTimer(t => t - 1), 1000);
    return () => { if (iRef.current) clearInterval(iRef.current); };
  }, [timer]);

  useEffect(() => {
    if (!open) { setStep("phone"); setPhone(""); setOtp(""); setTimer(0); setError(""); }
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const handleSend = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) { setError("Enter a valid 10-digit Indian mobile number"); return; }
    setError(""); setLoading(true);
    try {
      await sendOTP(phone);
      setStep("otp"); setTimer(60);
    } catch (e: any) { setError(e.message || "Failed to send OTP. Try again."); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setError(""); setLoading(true);
    try {
      const data = await verifyOTP(phone, otp);
      sellStore.setPhone(phone);
      sellStore.setVerified(true, data.customer._id);
      authStore.login(data.token, { _id: data.customer._id, phone });
      onClose();
      navigate("/questionnaire");
    } catch (e: any) { setError(e.message || "Incorrect OTP. Please try again."); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setOtp(""); setTimer(60); setError("");
    try { await sendOTP(phone); } catch (e: any) { setError(e.message || "Failed to resend."); }
  };

  return (
    <div style={{ position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(17,24,39,0.5)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",padding:"16px",animation:"fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%",maxWidth:420,background:"#fff",borderRadius:20,padding:"32px 28px",boxShadow:"0 24px 60px rgba(0,0,0,0.18)",animation:"scaleIn 0.28s cubic-bezier(0.34,1.56,0.64,1)",position:"relative" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24 }}>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:6 }}>
              <span style={{ width:8,height:8,borderRadius:"50%",background:"var(--brand)",display:"inline-block" }} />
              <span style={{ fontSize:11,fontWeight:700,color:"var(--brand)",letterSpacing:"0.08em",textTransform:"uppercase" }}>Secure Verification</span>
            </div>
            <h3 style={{ fontFamily:"var(--font-display)",fontWeight:700,fontSize:22,color:"var(--text)",marginBottom:4 }}>
              {step === "phone" ? "Verify Your Number" : "Enter OTP"}
            </h3>
            <p style={{ fontSize:13,color:"var(--text2)" }}>
              {step === "phone" ? "We'll send a 6-digit code on your WhatsApp" : `OTP sent on WhatsApp to +91 ${phone}`}
            </p>
          </div>
          <button onClick={onClose} style={{ width:32,height:32,borderRadius:8,border:"1.5px solid var(--border)",background:"var(--bg)",color:"var(--text2)",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>✕</button>
        </div>

        {step === "phone" ? (
          <>
            <div style={{ display:"flex",alignItems:"center",gap:10,background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:12,padding:"12px 14px" }}>
              <span style={{ background:"var(--surface2)",borderRadius:7,padding:"4px 10px",fontSize:13,fontWeight:600,color:"var(--text2)",whiteSpace:"nowrap",flexShrink:0 }}>+91</span>
              <input type="tel" maxLength={10} value={phone} autoFocus
                onChange={e => { setPhone(e.target.value.replace(/\D/g,"")); setError(""); }}
                onFocus={e => { (e.target.parentElement as HTMLElement).style.borderColor="var(--brand)"; }}
                onBlur={e => { (e.target.parentElement as HTMLElement).style.borderColor="var(--border)"; }}
                placeholder="10-digit mobile number"
                style={{ flex:1,background:"transparent",border:"none",outline:"none",fontSize:15,color:"var(--text)",fontFamily:"var(--font-body)",minWidth:0 }} />
            </div>
            {error && <p style={{ marginTop:8,fontSize:13,color:"var(--danger)" }}>⚠ {error}</p>}
            <button onClick={handleSend} disabled={loading}
              style={{ marginTop:14,width:"100%",borderRadius:12,background:loading?"var(--surface2)":"var(--brand)",color:loading?"var(--text2)":"#fff",padding:"13px",fontSize:15,fontWeight:600,cursor:loading?"not-allowed":"pointer",border:"none",transition:"all 0.2s",boxShadow:loading?"none":"var(--shadow-glow)",fontFamily:"var(--font-body)" }}>
              {loading ? "Sending…" : "Send OTP →"}
            </button>
          </>
        ) : (
          <>
            <input type="text" inputMode="numeric" maxLength={6} value={otp} autoFocus
              onChange={e => { setOtp(e.target.value.replace(/\D/g,"")); setError(""); }}
              placeholder="• • • • • •"
              style={{ width:"100%",textAlign:"center",letterSpacing:"0.5em",fontSize:26,fontWeight:700,color:"var(--text)",background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:12,padding:"14px",outline:"none",fontFamily:"var(--font-display)",transition:"border-color 0.2s" }}
              onFocus={e => e.target.style.borderColor="var(--brand)"}
              onBlur={e => e.target.style.borderColor="var(--border)"} />
            {error && <p style={{ marginTop:8,fontSize:13,color:"var(--danger)" }}>⚠ {error}</p>}
            <button onClick={handleVerify} disabled={loading || otp.length !== 6}
              style={{ marginTop:14,width:"100%",borderRadius:12,background:(loading||otp.length!==6)?"var(--surface2)":"var(--brand)",color:(loading||otp.length!==6)?"var(--text2)":"#fff",padding:"13px",fontSize:15,fontWeight:600,cursor:(loading||otp.length!==6)?"not-allowed":"pointer",border:"none",transition:"all 0.2s",fontFamily:"var(--font-body)" }}>
              {loading ? "Verifying…" : "Verify & Continue →"}
            </button>
            <div style={{ marginTop:12,textAlign:"center",fontSize:13,color:"var(--text2)" }}>
              {timer > 0
                ? <>Resend in <strong style={{ color:"var(--brand)" }}>{timer}s</strong></>
                : <button onClick={handleResend} style={{ background:"none",border:"none",color:"var(--brand)",fontWeight:600,cursor:"pointer",fontSize:13,fontFamily:"var(--font-body)" }}>Resend OTP</button>}
            </div>
          </>
        )}
        <p style={{ marginTop:18,fontSize:12,color:"var(--text3)",textAlign:"center",lineHeight:1.5 }}>
          🔒 Your number is encrypted and never shared with third parties
        </p>
      </div>
    </div>
  );
}
